import React, { useCallback, useEffect, useState } from 'react'
import { ApiError, getChallengeAnalytics } from '../api/orchestratorClient'

/** Item 2: how often developers challenge an AI suggestion -- backed by
 *  GET /admin/mbo/analytics/challenges (essms-mbo's Analysis.isChallengeReassessment/
 *  challengedBy columns, changeset 36). Plain CSS bars rather than D3 -- App.jsx's
 *  D3 chart components aren't exported for reuse outside OverviewDashboard, and this
 *  view's two charts (a small bar-per-week strip, a per-challenger breakdown list)
 *  don't need D3's machinery. */
export function ChallengeAnalytics() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setData(await getChallengeAnalytics())
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not reach the orchestrator API. Is it running?')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  if (loading) return <div className="panel-loading">Loading…</div>
  if (error) return <div className="review-error" role="alert">⚠️ {error}</div>
  if (!data) return null

  const byWeek = Object.entries(data.byWeek || {})
  const maxWeek = Math.max(1, ...byWeek.map(([, v]) => v))
  const byChallenger = Object.entries(data.byChallenger || {}).sort((a, b) => b[1] - a[1])
  const maxChallenger = Math.max(1, ...byChallenger.map(([, v]) => v))
  const ratePct = Number(data.challengeRatePct || 0)

  return (
    <div className="challenge-analytics">
      <div className="ca-header">
        <h2>AI Suggestion Challenges</h2>
        <p className="ca-subtitle">
          How often developers push back on an AI analysis and ask for a fresh, more
          careful pass -- a direct signal of where the AI's first-draft judgement is
          weakest.
        </p>
      </div>

      <div className="stat-tiles">
        <div className="stat-tile">
          <span className="stat-label">Total analyses</span>
          <span className="stat-value">{Number(data.totalAnalyses || 0).toLocaleString()}</span>
        </div>
        <div className="stat-tile">
          <span className="stat-label">Total challenged</span>
          <span className="stat-value">{Number(data.totalChallenged || 0).toLocaleString()}</span>
        </div>
        <div className="stat-tile">
          <span className="stat-label">Challenge rate</span>
          <span className="stat-value" style={{ color: ratePct > 15 ? 'var(--status-warning)' : 'var(--status-good)' }}>
            {ratePct.toFixed(1)}%
          </span>
        </div>
      </div>

      <div className="ca-section">
        <h3>Challenges per week</h3>
        {byWeek.length === 0 ? <p className="ca-empty">No data yet.</p> : (
          <div className="week-chart">
            {byWeek.map(([week, count]) => (
              <div key={week} className="week-bar-col" title={`${week}: ${count} challenge(s)`}>
                <div className="week-bar" style={{ height: `${Math.max(4, (count / maxWeek) * 100)}%` }} />
                <span className="week-count">{count}</span>
                <span className="week-label">{week.slice(5)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="ca-section">
        <h3>By developer</h3>
        {byChallenger.length === 0 ? <p className="ca-empty">No challenges recorded yet.</p> : (
          <div className="challenger-list">
            {byChallenger.map(([who, count]) => (
              <div key={who} className="challenger-row">
                <span className="challenger-name">{who}</span>
                <div className="challenger-track">
                  <div className="challenger-fill" style={{ width: `${(count / maxChallenger) * 100}%` }} />
                </div>
                <span className="challenger-count">{count}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <style jsx>{`
        .challenge-analytics {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        .ca-header h2 {
          font-size: 18px;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 4px;
        }
        .ca-subtitle {
          font-size: 13px;
          color: var(--text-secondary);
          max-width: 620px;
        }
        .stat-tiles {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 10px;
        }
        .stat-tile {
          display: flex;
          flex-direction: column;
          gap: 6px;
          padding: 14px 16px;
          border: 1px solid var(--border);
          border-radius: 8px;
          background: var(--surface-1);
        }
        .stat-label {
          font-size: 11px;
          font-weight: 700;
          color: var(--text-muted);
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .stat-value {
          font-size: 24px;
          font-weight: 700;
          color: var(--text-primary);
          font-variant-numeric: tabular-nums;
        }
        .ca-section {
          background: var(--surface-1);
          border: 1px solid var(--border);
          border-radius: 8px;
          padding: 16px;
        }
        .ca-section h3 {
          font-size: 13px;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 12px;
        }
        .ca-empty {
          font-size: 13px;
          color: var(--text-muted);
        }
        .week-chart {
          display: flex;
          align-items: flex-end;
          gap: 8px;
          height: 140px;
        }
        .week-bar-col {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: flex-end;
          height: 100%;
          gap: 4px;
        }
        .week-bar {
          width: 100%;
          max-width: 36px;
          border-radius: 4px 4px 0 0;
          background: var(--series-1);
        }
        .week-count {
          font-size: 11px;
          font-weight: 700;
          color: var(--text-primary);
          font-variant-numeric: tabular-nums;
        }
        .week-label {
          font-size: 10px;
          color: var(--text-muted);
          white-space: nowrap;
        }
        .challenger-list {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .challenger-row {
          display: grid;
          grid-template-columns: 140px 1fr 36px;
          align-items: center;
          gap: 10px;
        }
        .challenger-name {
          font-size: 12px;
          color: var(--text-primary);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .challenger-track {
          height: 8px;
          border-radius: 999px;
          background: var(--border);
          overflow: hidden;
        }
        .challenger-fill {
          height: 100%;
          border-radius: 999px;
          background: var(--series-1);
        }
        .challenger-count {
          font-size: 12px;
          font-weight: 700;
          color: var(--text-primary);
          text-align: right;
          font-variant-numeric: tabular-nums;
        }
        .panel-loading, .review-error {
          padding: 16px;
          font-size: 13px;
        }
        .review-error {
          background: #fee2e2;
          color: #991b1b;
          border-radius: 6px;
        }
      `}</style>
    </div>
  )
}
